import { useState } from 'react';
import { Record as RaRecord, useDataProvider, useNotify } from 'react-admin';
import { MarkAsPaidSummaryInterface } from '@vatos-pas/common';

import { MarkAsPaidConfirmProps } from './MarkAsPaidConfirm';

/** A sheet's Mark as Paid batch endpoint and the notes its bodies add. */
export type MarkAsPaidTarget<Body> = {
  /** Batch resource; its `/summary` sibling takes the same body. */
  resource: string;
  /** Red notes the prompt adds for a body, after the paid rows warning. */
  warningsFor?: (body: Body) => string[];
};

type Prompt<Body> = {
  body: Body;
  summary: MarkAsPaidSummaryInterface;
  warnings: string[];
};

/**
 * Mark as Paid confirmation state. `open` builds the body once and summarizes
 * it; Confirm hands `onConfirm` that same body, so the run covers exactly the
 * summarized rows. A failed build or summary notifies and stays closed.
 */
export const useMarkAsPaidPrompt = <Body>(
  { resource, warningsFor }: MarkAsPaidTarget<Body>,
  onConfirm: (body: Body) => void,
) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  // Kept after close, so the dialog keeps its content while it fades out.
  const [prompt, setPrompt] = useState<Prompt<Body> | null>(null);

  const open = async (buildBody: () => Body | Promise<Body>) => {
    setIsLoading(true);
    try {
      const body = await buildBody();
      const { data: summary } = await dataProvider.create<
        RaRecord & MarkAsPaidSummaryInterface
      >(`${resource}/summary`, { data: body });
      setPrompt({ body, summary, warnings: warningsFor?.(body) ?? [] });
      setIsOpen(true);
    } catch (error) {
      notify(error.message || 'Failed to load the summary.', 'warning');
    } finally {
      setIsLoading(false);
    }
  };

  const close = () => setIsOpen(false);

  const dialogProps: Pick<
    MarkAsPaidConfirmProps,
    'isOpen' | 'summary' | 'warnings' | 'onConfirm' | 'onClose'
  > = {
    isOpen,
    summary: prompt?.summary ?? null,
    warnings: prompt?.warnings,
    onConfirm: () => {
      if (prompt) onConfirm(prompt.body);
    },
    onClose: close,
  };

  return { isLoading, open, close, dialogProps };
};
